import { useRef, useState, useSyncExternalStore } from "react";
import { isViewTransitionRunning, subscribeViewTransitions } from "@/lib/viewTransitions";
import { readHandoff, type SharedHandoff } from "@/utils/sharedElements";

const idleSnapshot = () => !isViewTransitionRunning();
const serverSnapshot = () => true;

/** False while a view transition is animating. One update at start, one at the end. */
export function useViewTransitionIdle(): boolean {
  return useSyncExternalStore(subscribeViewTransitions, idleSnapshot, serverSnapshot);
}

/**
 * What the page we came from knew about this page's hero (cover and title),
 * read once on mount so the first commit can paint it before any data loads.
 */
export function useSharedArrival(): SharedHandoff | null {
  // window.location rather than useLocation: it is already the new URL on the
  // first render of a navigation, and release pages also render outside a router.
  const [handoff] = useState(() => (typeof window === "undefined" ? null : readHandoff(window.location.pathname)));
  return handoff;
}

/**
 * True while the transition that brought us here is still running, so a page
 * can keep its first screen (the one being morphed into) until it ends.
 * Only the arrival counts: once the page has been idle, the transition that
 * later takes it away must see the page exactly as it is.
 */
export function useHoldForArrival(handoff: SharedHandoff | null): boolean {
  const idle = useViewTransitionIdle();
  const settled = useRef(false);
  if (idle) settled.current = true;
  return Boolean(handoff) && !settled.current;
}
